import Link from 'next/link'

import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { AdminUser, Order, StoreSettings } from '@/types'
import type { AdminStats } from '@/lib/admin/data'

import { cn } from '@/lib/utils'
import { formatDateTime, formatIDR } from '@/lib/format'
import { getStoreSettings } from '@/lib/catalog'
import { adminFromContext, countAdmins } from '@/lib/admin/auth'
import { getAdminStats, getOrders } from '@/lib/admin/data'
import { AdminLayout } from '@/components/admin/admin-layout'
import { AdminLogin } from '@/components/admin/admin-login'
import { OrderStatusPill } from '@/components/admin/order-status'
import { buttonClass, Card } from '@/components/admin/admin-ui'

import {
    ArrowRight,
    CircleCheck,
    ClipboardList,
    Layers,
    Package,
    PackageX,
    PackagePlus,
    Settings,
    TriangleAlert
} from 'lucide-react'

type DashboardProps = {
    view: 'dashboard'
    admin: AdminUser
    settings: StoreSettings
    stats: AdminStats
    recentOrders: Array<Order>
} | {
    view: 'login'
    needsSetup: boolean
    settings: StoreSettings
}

const STAT_TONES = ['bg-blush-100 text-blush-600', 'bg-[#e4eedf] text-[#3f6638]', 'bg-[#f8e0dc] text-[#9c3b34]', 'bg-peach-100 text-[#a3552f]', 'bg-cream-200 text-cocoa-700']

const Dashboard: FC<DashboardProps> = (props): ReactNode => {
    if (props.view === 'login') return <AdminLogin needsSetup={props.needsSetup} />

    const { admin, settings, stats, recentOrders } = props

    const cards = [
        { label: 'Total produk', value: stats.totalProducts, icon: Package, href: '/admin/produk', note: stats.inactiveProducts ? `${stats.inactiveProducts} draf` : 'Semua diterbitkan' },
        { label: 'Produk tersedia', value: stats.availableProducts, icon: CircleCheck, href: '/admin/produk?status=tersedia', note: 'Diterbitkan dan bisa dipesan' },
        { label: 'Produk habis', value: stats.soldOutProducts, icon: PackageX, href: '/admin/produk?status=habis', note: stats.soldOutProducts ? 'Perlu diisi ulang' : 'Stok aman' },
        { label: 'Total kategori', value: stats.totalCategories, icon: Layers, href: '/admin/kategori', note: 'Kelola koleksi' },
        { label: 'Total pesanan', value: stats.totalOrders, icon: ClipboardList, href: '/admin/pesanan', note: stats.newOrders ? `${stats.newOrders} pesanan baru` : 'Tidak ada pesanan baru' }
    ]

    return (
        <AdminLayout
            admin={admin}
            title={`Halo, ${admin.name.split(' ')[0]}`}
            description='Ringkasan toko hari ini. Semua perubahan di dashboard langsung tampil di website.'
            newOrders={stats.newOrders}
            actions={(
                <Link href='/admin/produk/baru' className={buttonClass('primary', 'md')}>
                    <PackagePlus className='size-4' />
                    Tambah Produk
                </Link>
            )}
        >
            {!settings.whatsappNumber && (
                <div className='mb-6 flex flex-col gap-3 rounded-[24px] border border-[#f1d6a6] bg-[#fdf3dd] p-5 sm:flex-row sm:items-center sm:justify-between'>
                    <div className='flex items-start gap-3'>
                        <TriangleAlert className='mt-0.5 size-5 shrink-0 text-[#8a5a12]' />
                        <div>
                            <p className='text-sm font-bold text-[#6b4510]'>Nomor WhatsApp toko belum diatur</p>
                            <p className='mt-0.5 text-sm text-[#8a5a12]'>Pesanan pelanggan belum bisa langsung masuk ke WhatsApp toko sampai nomornya diisi.</p>
                        </div>
                    </div>
                    <Link href='/admin/pengaturan' className={cn(buttonClass('primary', 'sm'), 'shrink-0')}>
                        <Settings className='size-4' />
                        Atur sekarang
                    </Link>
                </div>
            )}

            <div className='grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-5'>
                {cards.map((card, index) => {
                    const Icon = card.icon

                    return (
                        <Link
                            key={card.label}
                            href={card.href}
                            className={cn(
                                'group rounded-[24px] border border-border/80 bg-white p-4 shadow-[0_18px_40px_-34px_rgba(58,40,30,0.45)] transition hover:-translate-y-0.5 hover:border-cocoa-700/20 sm:p-5',
                                index === cards.length - 1 && 'col-span-2 lg:col-span-1'
                            )}
                        >
                            <span className={cn('grid size-10 place-items-center rounded-2xl', STAT_TONES[index % STAT_TONES.length])}>
                                <Icon className='size-5' />
                            </span>
                            <p className='mt-4 font-heading text-3xl text-cocoa-900 tabular-nums'>{card.value}</p>
                            <p className='mt-1 text-sm font-semibold text-cocoa-800'>{card.label}</p>
                            <p className='mt-0.5 text-xs text-muted-foreground'>{card.note}</p>
                        </Link>
                    )
                })}
            </div>

            <div className='mt-6 grid gap-6 lg:grid-cols-[1.6fr_1fr]'>
                <Card
                    title='Pesanan terbaru'
                    description='Pesanan tercatat setiap kali pelanggan menekan tombol pesan WhatsApp.'
                    action={(
                        <Link href='/admin/pesanan' className={buttonClass('secondary', 'sm')}>
                            Lihat semua
                            <ArrowRight className='size-4' />
                        </Link>
                    )}
                >
                    {recentOrders.length === 0
                        ? (
                            <div className='rounded-2xl border border-dashed border-border px-5 py-10 text-center'>
                                <ClipboardList className='mx-auto size-8 text-cocoa-500/60' />
                                <p className='mt-3 text-sm font-semibold text-cocoa-800'>Belum ada pesanan</p>
                                <p className='mt-1 text-xs text-muted-foreground'>Pesanan pertama akan muncul di sini.</p>
                            </div>
                        )
                        : (
                            <ul className='divide-y divide-border'>
                                {recentOrders.map(order => (
                                    <li key={order.id}>
                                        <Link href={`/admin/pesanan#${order.orderCode}`} className='flex items-center justify-between gap-3 py-3.5 transition hover:opacity-80'>
                                            <div className='min-w-0'>
                                                <p className='flex flex-wrap items-center gap-2 text-sm font-bold text-cocoa-900'>
                                                    {order.orderCode}
                                                    <OrderStatusPill status={order.status} />
                                                </p>
                                                <p className='mt-0.5 truncate text-xs text-muted-foreground'>
                                                    {order.customerName ? <><span data-mdlfy-cms={`orders:${order.id}:customer_name`}>{order.customerName}</span> · </> : null}
                                                    <span>{order.itemCount}</span> produk · {formatDateTime(order.createdAt)}
                                                </p>
                                            </div>
                                            <p className='shrink-0 text-sm font-bold text-cocoa-800'>{formatIDR(order.total)}</p>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        )}
                </Card>

                <Card title='Akses cepat'>
                    <div className='grid gap-2'>
                        {[
                            { href: '/admin/produk/baru', label: 'Tambah produk baru', icon: PackagePlus },
                            { href: '/admin/produk?status=habis', label: 'Perbarui stok yang habis', icon: PackageX },
                            { href: '/admin/kategori', label: 'Atur kategori', icon: Layers },
                            { href: '/admin/pengaturan', label: 'Ubah logo dan info toko', icon: Settings }
                        ].map(item => {
                            const Icon = item.icon

                            return (
                                <Link key={item.href} href={item.href} className='flex items-center gap-3 rounded-2xl border border-border/70 px-4 py-3 text-sm font-semibold text-cocoa-800 transition hover:border-cocoa-700/20 hover:bg-cream-50'>
                                    <Icon className='size-5 text-blush-600' />
                                    <span className='flex-1'>{item.label}</span>
                                    <ArrowRight className='size-4 text-cocoa-500' />
                                </Link>
                            )
                        })}
                    </div>
                    <p className='mt-5 rounded-2xl bg-cream-100 px-4 py-3 text-xs leading-relaxed text-cocoa-700'>
                        Total nilai pesanan tercatat, di luar yang dibatalkan: <span className='font-bold'>{formatIDR(stats.orderValue)}</span>
                    </p>
                </Card>
            </div>
        </AdminLayout>
    )
}

export const getServerSideProps: GetServerSideProps<DashboardProps> = async context => {
    const settings = await getStoreSettings()
    const admin = await adminFromContext(context)

    if (!admin) {
        const needsSetup = await countAdmins().then(total => total === 0).catch(() => false)

        return { props: { view: 'login', needsSetup, settings } }
    }

    try {
        const [stats, recentOrders] = await Promise.all([getAdminStats(), getOrders({ limit: 6 })])

        return { props: { view: 'dashboard', admin, settings, stats, recentOrders } }
    }
    catch (error) {
        console.error('Could not load the dashboard:', error instanceof Error ? error.message : error)

        return {
            props: {
                view: 'dashboard',
                admin,
                settings,
                stats: { totalProducts: 0, availableProducts: 0, soldOutProducts: 0, inactiveProducts: 0, totalCategories: 0, totalOrders: 0, newOrders: 0, orderValue: 0 },
                recentOrders: []
            }
        }
    }
}

export default Dashboard
